/**
 * The Import hand-off every "bring files in" surface shares: the Upload Files
 * modal and a drop on the workspace canvas. It hands the files to the
 * background upload manager (the resumable transport, the tray, processing
 * status) as an `import` and says so in a toast; the caller never waits.
 *
 * `parent` is where the files land: a collection, or the workspace root.
 */

import { useCallback } from 'react';
import { MAX_FILES_PER_IMPORT } from '@bower/shared/schemas/upload.schema';
import type { CAPTURE_SURFACE_ONBOARDING_IMPORT } from '@bower/shared';

import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useUploadManagerStore } from '../../stores/uploadManagerStore';
import type { CreationTrigger } from '../../types/analytics';
import { safeAnalytics } from '../../utils/analytics';
import { useAppToast } from '../ui/useAppToast';

/** Files per import, the same cap for the modal and a drop. */
export const IMPORT_MAX_FILES = MAX_FILES_PER_IMPORT;

export interface ImportParent {
  readonly type: 'project' | 'workspace';
  readonly id: string;
}

export interface ImportFilesOptions {
  readonly parent: ImportParent;
  readonly trigger: CreationTrigger;
  /** Note target for AI extraction; 'note' when omitted. */
  readonly targetArtifactType?: string;
  readonly captureSurface?: typeof CAPTURE_SURFACE_ONBOARDING_IMPORT;
}

export function useImportFiles() {
  const { workspace } = useWorkspace();
  const toast = useAppToast();
  const enqueue = useUploadManagerStore((s) => s.enqueue);

  /** False when there is nothing to hand over (no workspace, no files). */
  return useCallback(
    (files: File[], options: ImportFilesOptions): boolean => {
      const { parent, trigger, targetArtifactType = 'note', captureSurface } = options;
      if (!workspace || files.length === 0) return false;
      const screen = parent.type === 'project' ? 'collection_detail' : 'workspace_artifacts';

      safeAnalytics.track(ANALYTICS_EVENTS.IMPORT_INITIATED, {
        workspace_id: workspace.id,
        import_type: 'import',
        creation_method: 'import',
        creation_screen: screen,
        creation_trigger: trigger,
        file_count: files.length,
        parent_entity_type: parent.type,
        target_artifact_type: targetArtifactType,
        file_types: files.map((f) => f.type).filter(Boolean),
      });

      enqueue(files, {
        workspaceId: workspace.id,
        intent: 'import',
        parentEntityType: parent.type,
        parentEntityId: parent.id,
        captureSurface,
        ...(targetArtifactType !== 'note' ? { targetArtifactType } : {}),
        onUploaded: (_result, file) => {
          // COMPLETED fires here, per file, on actual finalize, so the
          // INITIATED to COMPLETED funnel reflects real success, not clicks.
          safeAnalytics.track(ANALYTICS_EVENTS.IMPORT_COMPLETED, {
            workspace_id: workspace.id,
            import_type: 'import',
            creation_method: 'import',
            creation_screen: screen,
            file_type: file.type,
            parent_entity_type: parent.type,
            target_artifact_type: targetArtifactType,
          });
        },
      });

      toast({
        title: 'Import started',
        description: `${files.length} file${
          files.length === 1 ? '' : 's'
        } uploading in the background. You can keep working.`,
        status: 'info',
        duration: 5000,
      });
      return true;
    },
    [workspace, toast, enqueue],
  );
}
